import Offer_Creation_Action from '../../partials/offerCreation/Offer_Creation_Action';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Confirmation_, Main_Plain, Red_, device, PageAlwaysVisible, PageSixty, PageTitle } from 'monica-alexandria';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { New_Offer_Preview_Page } from './components/New_Offer_Preview_Page';
import New_Offer_Setup from './components/New_Offer_Setup';
import { Steps_ } from './components/Steps_';
import New_Offer_Types from './components/New_Offer_Types';
import { New_Offer_Price } from './components/New_Offer_Price';
import {
    confirm_offer_replacement,
    create_fake_offer,
    create_agency_help_offer,
    create_agency_sent_offer,
    create_offer_draft,
    delete_agency_offer_by_id,
    delete_offer_by_id,
    get_agency_preoffer_session,
    get_agency_offer_by_id,
    get_offer_draft_by_id,
    retry_agency_preoffer_sync_by_id,
    send_agency_offer_by_id,
    submit_agency_preoffer_by_id,
    submit_agency_help_estimate_by_id,
    update_offer_draft_by_id
} from '../../../services/api/Offers';
import { checkPermissions } from '../../../helpers/checkPermissions';
import { get_route_language } from '../../../helpers/routeLanguage';
import { useHrAccess } from '../../../services/authentication/HrAccess';
import { offer_uses_cerebro_handoff } from '../../../helpers/preoffer_handoff';
import {
    offer_visit_count,
    selected_types_from_offer
} from '../../../helpers/offerObject';
import {
    continue_offer_start_step,
    draft_save_next_step,
    fresh_offer_selected_types,
    handoff_prefill_should_apply,
    help_estimate_setup_payload,
    legacy_preoffer_issue_review_state,
    new_offer_main_loader_is_visible,
    offer_catalog_type_key,
    offer_total_is_positive,
    offer_workflow_mode,
    preoffer_department_board_from_types,
    preoffer_coordinator_can_continue,
    preoffer_department_estimate_turn,
    preoffer_session_uses_legacy_workflow,
    request_offer_setup_mode,
    reviewer_suggested_pricing_is_complete,
    stored_offer_can_resume
} from './utils/offer_creation_rules';
const offerDraftStorageKey = 'godfather_offer_draft_id';
const offerDraftPayloadStorageKey = 'godfather_offer_local_draft_payload';
const offerHandoffTimeoutMs = 60000;
const agencyOfferWorkflowsEnabled = true;

const NewOffer = styled.div`
    margin-left: 30rem;

    @media ${device.md} {
        margin-left: 30rem;
    }

    @media ${device.sm} {
        margin-left: 0;
        padding-top: 34rem;
    }
`;

const NewOfferContent = styled(PageSixty)``;

const NewOfferTitleRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.2rem;
`;

const NewOfferTopActions = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
`;

const NewOfferMainLoader = styled.div`
    width: 100%;
    min-height: 18rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.2rem;
    color: ${p => p.theme.color};
`;

const NewOfferMainLoaderIcon = styled.div`
    width: 4.8rem;
    height: 4.8rem;
    border: 0.3rem solid ${p => p.theme.low};
    border-top-color: var(--main);
    border-radius: 50%;
    animation: new_offer_main_loader_spin 0.8s linear infinite;

    @keyframes new_offer_main_loader_spin {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }
`;

const read_stored_offer_id = () => {
    try {
        return window.localStorage.getItem(offerDraftStorageKey) || '';
    } catch (error) {
        return '';
    }
};

const write_stored_offer_id = (offerId) => {
    try {
        window.localStorage.setItem(offerDraftStorageKey, offerId);
    } catch (error) {
    }
};

const clear_stored_offer_id = () => {
    try {
        window.localStorage.removeItem(offerDraftStorageKey);
        window.localStorage.removeItem(offerDraftPayloadStorageKey);
    } catch (error) {
    }
};

const read_stored_offer_draft = () => {
    try {
        const rawDraft = window.localStorage.getItem(offerDraftPayloadStorageKey) || '';

        if (!rawDraft.length) {
            return null;
        }

        return JSON.parse(rawDraft);
    } catch (error) {
        return null;
    }
};

const write_stored_offer_draft = (offerDraft = {}) => {
    try {
        window.localStorage.setItem(offerDraftPayloadStorageKey, JSON.stringify(offerDraft || {}));
    } catch (error) {
    }
};

const offer_storage_prefix = 'godfather_offer_';
const offer_session_prefix = 'godfather_offer_';
const remove_cookie_by_name = (cookieName = '') => {
    if (!cookieName.length) {
        return;
    }

    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

const clear_offer_related_browser_state = () => {
    clear_stored_offer_id();

    try {
        const localStorageKeys = Object.keys(window.localStorage || {});

        localStorageKeys.forEach((storageKey) => {
            if (storageKey.startsWith(offer_storage_prefix)) {
                window.localStorage.removeItem(storageKey);
            }
        });
    } catch (error) {
    }

    try {
        const sessionStorageKeys = Object.keys(window.sessionStorage || {});

        sessionStorageKeys.forEach((storageKey) => {
            if (storageKey.startsWith(offer_session_prefix)) {
                window.sessionStorage.removeItem(storageKey);
            }
        });
    } catch (error) {
    }

    try {
        const cookieNames = `${document.cookie || ''}`
            .split(';')
            .map((cookieItem) => cookieItem.trim())
            .filter(Boolean)
            .map((cookieItem) => cookieItem.split('=')[0] || '');

        cookieNames.forEach((cookieName) => {
            if (cookieName.startsWith(offer_storage_prefix)) {
                remove_cookie_by_name(cookieName);
            }
        });
    } catch (error) {
    }
};

const create_default_offer_payload = () => {
    const requestOfferCode = '';
    const defaultSelectedTypes = fresh_offer_selected_types();

    return {
        ofrId: requestOfferCode,
        selectedType: defaultSelectedTypes[0] || '',
        language: 'el',
        invoice: true,
        submittedBy: '',
        deleted: false,
        client: {
            emails: [],
            numbers: [],
            category: '',
            subcategory: '',
            ownerFirstname: '',
            ownerLastname: '',
            location: '',
            vat: ''
        },
        selectedTypes: [...defaultSelectedTypes],
        clientEmails: [],
        category: '',
        subcategory: '',
        industry: '',
        clientName: '',
        clientHeadquarters: '',
        clientVatNumber: '',
        offerLanguage: 'el',
        code: requestOfferCode,
        typesData: {
            selectedTypes: [...defaultSelectedTypes],
            clientEmails: [],
            category: '',
            subcategory: '',
            industry: '',
            clientName: '',
            clientHeadquarters: '',
            clientVatNumber: '',
            offerLanguage: 'el',
            invoiceEnabled: true,
            offerCode: requestOfferCode,
            requestOfferMode: false,
            sourceSystem: '',
            sourceRequestId: '',
            preofferEnabled: false,
            preofferAssignedTeamLead: null,
            preofferRequestedAt: '',
            preofferRequestedBy: null,
            preofferTurn: '',
            preofferEstimateSubmittedAt: '',
            preofferEstimateSubmittedBy: null,
            preofferDepartmentBoard: ''
        },
        setupData: {},
        priceData: {},
        niffler: {},
        timeline: [],
        visits: [],
        status: 'draft',
        active: true
    };
};

const normalize_industry_value = (industryValue) => {
    const normalizedIndustryValue = `${industryValue || ''}`.trim().toLowerCase();

    if (normalizedIndustryValue === 'general') {
        return '';
    }

    return normalizedIndustryValue;
};

const read_normalized_value = (value) => {
    return String(value || '').trim().toLowerCase();
};

const known_offer_type_keys = ['website_creation', 'website_maintenance', 'marketing', 'graphic', 'creative', 'sass', 'website'];

const read_service_type_from_key = (serviceKey = '') => {
    const normalizedServiceKey = read_normalized_value(serviceKey);
    const typeFromKnownKeys = known_offer_type_keys.find((typeName) => normalizedServiceKey.startsWith(`${typeName}_`));

    if (typeFromKnownKeys) {
        return typeFromKnownKeys;
    }

    return normalizedServiceKey.split('_')[0] || '';
};

const first_selected_types = (...typeLists) => {
    return typeLists.find((typeList) => {
        return Array.isArray(typeList) && typeList.length;
    }) || [];
};

const read_member_payload = (memberItem = {}) => {
    const subId = memberItem?.subId || memberItem?.sub || memberItem?.userId || memberItem?.id || '';

    return {
        _id: memberItem?._id || '',
        id: subId,
        userId: subId,
        subId,
        email: memberItem?.email || memberItem?.memberEmail || '',
        username: memberItem?.username || memberItem?.userName || memberItem?.memberUsername || memberItem?.preferred_username || '',
        name: memberItem?.name || memberItem?.fullName || '',
        role: memberItem?.role || '',
        department: memberItem?.department || ''
    };
};

const read_member_identity_keys = (memberItem = {}) => {
    return [
        memberItem?.userId,
        memberItem?.subId,
        memberItem?.id,
        memberItem?._id,
        memberItem?.sub,
        memberItem?.email,
        memberItem?.memberEmail,
        memberItem?.username,
        memberItem?.userName,
        memberItem?.memberUsername,
        memberItem?.preferred_username,
        memberItem?.name,
        memberItem?.fullName,
        memberItem?.value,
        memberItem?.label
    ]
        .map((identityValue) => read_normalized_value(identityValue))
        .filter(Boolean);
};

const has_meaningful_value = (value) => {
    if (value === undefined || value === null) {
        return false;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'object') {
        return Object.keys(value).length > 0;
    }

    return true;
};

const read_offer_scope_value = (offerData = {}, fieldName = '') => {
    const values = [
        offerData?.typesData?.[fieldName],
        offerData?.setupData?.[fieldName],
        offerData?.[fieldName]
    ];
    const firstNonEmptyValue = values.find((value) => has_meaningful_value(value));

    if (firstNonEmptyValue === undefined || firstNonEmptyValue === null) {
        return '';
    }

    return firstNonEmptyValue;
};

const read_offer_scope_boolean_true = (offerData = {}, fieldName = '') => {
    return [
        offerData?.typesData?.[fieldName],
        offerData?.setupData?.[fieldName],
        offerData?.[fieldName]
    ].some((value) => value === true);
};

const read_preoffer_assigned_identity_keys = (offerData = {}) => {
    const assignedTeamLead = read_offer_scope_value(offerData, 'preofferAssignedTeamLead');

    if (!assignedTeamLead) {
        return [];
    }

    if (typeof assignedTeamLead === 'string') {
        return [read_normalized_value(assignedTeamLead)].filter(Boolean);
    }

    return [
        assignedTeamLead?.userId,
        assignedTeamLead?.subId,
        assignedTeamLead?.id,
        assignedTeamLead?._id,
        assignedTeamLead?.email,
        assignedTeamLead?.memberEmail,
        assignedTeamLead?.username,
        assignedTeamLead?.userName,
        assignedTeamLead?.memberUsername,
        assignedTeamLead?.preferred_username,
        assignedTeamLead?.name,
        assignedTeamLead?.fullName,
        assignedTeamLead?.value,
        assignedTeamLead?.label
    ]
        .map((identityValue) => read_normalized_value(identityValue))
        .filter(Boolean);
};

const read_preoffer_turn = (offerData = {}) => {
    const normalizedTurn = read_normalized_value(read_offer_scope_value(offerData, 'preofferTurn') || '');

    if (normalizedTurn === 'coordinator') {
        return 'coordinator';
    }

    if (normalizedTurn === 'done') {
        return 'done';
    }

    return 'team_lead';
};

const read_is_preoffer_enabled = (offerData = {}) => {
    if (read_offer_scope_boolean_true(offerData, 'preofferEnabled')) {
        return true;
    }

    const requestedAt = `${read_offer_scope_value(offerData, 'preofferRequestedAt') || ''}`.trim();
    const normalizedTurn = read_normalized_value(read_offer_scope_value(offerData, 'preofferTurn') || '');
    const hasTurn = normalizedTurn === 'team_lead'
        || normalizedTurn === 'teamleader'
        || normalizedTurn === 'team_leader'
        || normalizedTurn === 'teamlead'
        || normalizedTurn === 'coordinator'
        || normalizedTurn === 'done';
    const hasOwner = Boolean(
        read_offer_scope_value(offerData, 'preofferAssignedTeamLead')
        || read_offer_scope_value(offerData, 'preofferRequestedBy')
    );

    return requestedAt.length > 0 || hasTurn || hasOwner;
};

const read_is_preoffer_requested = (offerData = {}) => {
    const preofferEnabled = read_is_preoffer_enabled(offerData);
    const requestedAt = `${read_offer_scope_value(offerData, 'preofferRequestedAt') || ''}`.trim();
    const normalizedTurn = read_normalized_value(read_offer_scope_value(offerData, 'preofferTurn') || '');
    const hasTurn = normalizedTurn === 'team_lead'
        || normalizedTurn === 'teamleader'
        || normalizedTurn === 'team_leader'
        || normalizedTurn === 'teamlead'
        || normalizedTurn === 'coordinator'
        || normalizedTurn === 'done';
    const hasOwner = Boolean(
        read_offer_scope_value(offerData, 'preofferAssignedTeamLead')
        || read_offer_scope_value(offerData, 'preofferRequestedBy')
    );

    if (!preofferEnabled) {
        return false;
    }

    if (requestedAt.length > 0) {
        return true;
    }

    return hasTurn && hasOwner;
};

const infer_selected_types_from_setup_data = (setupData = {}) => {
    const selectedServices = setupData?.selectedServices || {};
    const selectedServiceKeys = Object.keys(selectedServices).filter((serviceKey) => selectedServices[serviceKey] === true);
    const selectedTypes = selectedServiceKeys
        .map((serviceKey) => read_service_type_from_key(serviceKey))
        .filter(Boolean);

    return [...new Set(selectedTypes)];
};

const setup_data_has_website_maintenance = (setupData = {}) => {
    const selectedServices = setupData?.selectedServices || {};

    return Object.keys(selectedServices).some((serviceKey) => {
        return selectedServices[serviceKey] === true && read_normalized_value(serviceKey).startsWith('website_maintenance_');
    });
};

const normalize_selected_website_types = (selectedTypes = [], setupData = {}) => {
    const hasWebsiteMaintenance = setup_data_has_website_maintenance(setupData);

    return [...new Set(selectedTypes.map((typeName) => {
        const normalizedTypeName = offer_catalog_type_key(typeName);

        if (normalizedTypeName === 'website') {
            return hasWebsiteMaintenance ? 'website_maintenance' : 'website_creation';
        }

        return normalizedTypeName;
    }).filter(Boolean))];
};

const read_offer_types_for_permissions = (offerData = {}) => {
    const canonicalTypes = selected_types_from_offer(offerData);

    if (canonicalTypes.length) {
        return canonicalTypes;
    }

    const rootTypes = Array.isArray(offerData?.selectedTypes) ? offerData.selectedTypes : [];
    const typesDataTypes = Array.isArray(offerData?.typesData?.selectedTypes) ? offerData.typesData.selectedTypes : [];
    const setupTypes = infer_selected_types_from_setup_data(offerData?.setupData || {});

    return [...new Set(
        [...rootTypes, ...typesDataTypes, ...setupTypes]
            .map((typeItem) => read_normalized_value(typeItem))
            .filter(Boolean)
    )];
};

const read_string_fallback = (...values) => {
    const firstNonEmptyValue = values.find((value) => `${value || ''}`.trim().length > 0);

    if (!firstNonEmptyValue) {
        return '';
    }

    return `${firstNonEmptyValue}`.trim();
};

const read_array_fallback = (...values) => {
    const firstNonEmptyArray = values.find((value) => Array.isArray(value) && value.length > 0);

    if (!firstNonEmptyArray) {
        return [];
    }

    return firstNonEmptyArray;
};

const prefill_is_sass_import = (prefillData = {}) => {
    const sassData = prefillData?.sass || {};
    const typeValues = [
        prefillData?.selectedType,
        ...(Array.isArray(prefillData?.selectedTypes) ? prefillData.selectedTypes : []),
        ...(Array.isArray(prefillData?.typesData?.selectedTypes) ? prefillData.typesData.selectedTypes : [])
    ];
    const hasSassData = Object.keys(sassData).length > 0;
    const hasSassType = typeValues.map((typeValue) => read_normalized_value(typeValue)).includes('sass');
    const actionType = read_normalized_value(
        prefillData?.action?.type
        || sassData?.outcome
        || prefillData?.typesData?.sourceNifflerAction
        || ''
    );

    return actionType === 'sass' || hasSassData || hasSassType;
};

const read_object_fallback = (...values) => {
    const firstNonEmptyObject = values.find((value) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return false;
        }

        return Object.keys(value).length > 0;
    });

    if (!firstNonEmptyObject) {
        return {};
    }

    return firstNonEmptyObject;
};

const normalize_setup_data_for_legacy = (setupData = {}, priceData = {}, rootData = {}) => {
    const selectedServices = {
        ...read_object_fallback(setupData?.selectedServices, rootData?.selectedServices, priceData?.selectedServices)
    };
    const serviceComments = read_object_fallback(setupData?.serviceComments, rootData?.serviceComments, priceData?.serviceComments);
    const serviceHiddenMessages = read_object_fallback(
        setupData?.serviceHiddenMessages,
        rootData?.serviceHiddenMessages,
        priceData?.serviceHiddenMessages
    );
    const serviceOneOffValues = read_object_fallback(
        setupData?.serviceOneOffValues,
        rootData?.serviceOneOffValues,
        priceData?.serviceOneOffValues
    );
    const servicePriceOverwriteValues = read_object_fallback(
        setupData?.servicePriceOverwriteValues,
        rootData?.servicePriceOverwriteValues,
        priceData?.servicePriceOverwriteValues
    );
    const serviceDealValues = read_object_fallback(setupData?.serviceDealValues, rootData?.serviceDealValues, priceData?.serviceDealValues);
    const serviceAdSpendValues = read_object_fallback(
        setupData?.serviceAdSpendValues,
        rootData?.serviceAdSpendValues,
        priceData?.serviceAdSpendValues
    );
    const serviceWorkhoursValues = read_object_fallback(
        setupData?.serviceWorkhoursValues,
        rootData?.serviceWorkhoursValues,
        priceData?.serviceWorkhoursValues
    );
    const serviceSuggestedPricingValues = read_object_fallback(
        setupData?.serviceSuggestedPricingValues,
        rootData?.serviceSuggestedPricingValues
    );
    const selectedServiceSourceMaps = [
        serviceComments,
        serviceHiddenMessages,
        serviceOneOffValues,
        servicePriceOverwriteValues,
        serviceDealValues,
        serviceAdSpendValues,
        serviceWorkhoursValues,
        serviceSuggestedPricingValues
    ];

    selectedServiceSourceMaps.forEach((sourceMap) => {
        Object.keys(sourceMap || {}).forEach((serviceKey) => {
            const serviceValue = sourceMap?.[serviceKey];

            if (selectedServices[serviceKey] === false) {
                return;
            }

            if (!has_meaningful_value(serviceValue) && serviceValue !== false) {
                return;
            }

            selectedServices[serviceKey] = true;
        });
    });

    return {
        ...setupData,
        selectedServices,
        showServiceComments: read_object_fallback(setupData?.showServiceComments, rootData?.showServiceComments, priceData?.showServiceComments),
        showHiddenMessages: read_object_fallback(setupData?.showHiddenMessages, rootData?.showHiddenMessages, priceData?.showHiddenMessages),
        serviceComments,
        serviceHiddenMessages,
        serviceOneOffValues,
        servicePriceOverwriteValues,
        serviceDealValues,
        serviceAdSpendValues,
        serviceWorkhoursValues,
        serviceSuggestedPricingValues,
        customServicesByType: read_object_fallback(setupData?.customServicesByType, rootData?.customServicesByType, priceData?.customServicesByType),
        brandOverview: read_string_fallback(setupData?.brandOverview, priceData?.brandOverview, rootData?.brandOverview),
        showOverviewHiddenMessage: Boolean(
            setupData?.showOverviewHiddenMessage ?? priceData?.showOverviewHiddenMessage ?? rootData?.showOverviewHiddenMessage ?? false
        ),
        overviewHiddenMessage: read_string_fallback(
            setupData?.overviewHiddenMessage,
            priceData?.overviewHiddenMessage,
            rootData?.overviewHiddenMessage
        )
    };
};

const merge_price_data_with_setup = (priceData = {}, setupData = {}) => {
    return {
        ...priceData,
        brandOverview: `${setupData?.brandOverview ?? priceData?.brandOverview ?? ''}`.trim(),
        showOverviewHiddenMessage: Boolean(
            setupData?.showOverviewHiddenMessage ?? priceData?.showOverviewHiddenMessage ?? false
        ),
        overviewHiddenMessage: `${setupData?.overviewHiddenMessage ?? priceData?.overviewHiddenMessage ?? ''}`.trim()
    };
};

const read_value_from_scopes = (typesData = {}, setupData = {}, rootData = {}, fieldName = '') => {
    const values = [
        typesData?.[fieldName],
        setupData?.[fieldName],
        rootData?.[fieldName]
    ];
    const firstNonEmptyValue = values.find((value) => {
        if (value === undefined || value === null) {
            return false;
        }

        return has_meaningful_value(value);
    });

    if (firstNonEmptyValue === undefined || firstNonEmptyValue === null) {
        return '';
    }

    return firstNonEmptyValue;
};

const read_query_offer_prefill = (prefillValue = '') => {
    if (!prefillValue.length) {
        return null;
    }

    try {
        const parsedPrefill = JSON.parse(prefillValue);

        if (!parsedPrefill || typeof parsedPrefill !== 'object' || Array.isArray(parsedPrefill)) {
            return null;
        }

        return parsedPrefill;
    } catch (error) {
        return null;
    }
};

const read_prefill_offer_language = (prefillData = {}) => {
    const prefillTypesData = prefillData?.typesData || {};
    const normalizedLanguage = read_string_fallback(
        prefillTypesData?.offerLanguage,
        prefillData?.offerLanguage
    ).toLowerCase();

    if (normalizedLanguage === 'en') {
        return 'en';
    }

    return 'el';
};

const read_prefill_is_niffler = (prefillTypesData = {}) => {
    const sourceSystem = read_normalized_value(prefillTypesData?.sourceSystem);

    return sourceSystem === 'niffler' || sourceSystem === 'niffler-prospect';
};

const prefill_has_niffler_agency_identity = (prefillData = {}) => {
    const typesData = prefillData?.typesData || {};

    return read_prefill_is_niffler(typesData)
        && `${typesData?.sourceProspectId || ''}`.trim().length > 0
        && `${typesData?.sourceBrandId || ''}`.trim().length > 0;
};

const read_prefill_init_key = (handoffId = '', prefillValue = '') => {
    const normalizedHandoffId = `${handoffId || ''}`.trim();
    const normalizedPrefillValue = `${prefillValue || ''}`.trim();

    if (normalizedHandoffId.length) {
        return `handoff:${normalizedHandoffId}`;
    }

    if (normalizedPrefillValue.length) {
        return `prefill:${normalizedPrefillValue}`;
    }

    return '';
};

const create_prefilled_offer_payload = (prefillData = {}) => {
    const defaultOfferPayload = create_default_offer_payload();
    const normalizedPrefillData = prefillData;
    const prefillTypesData = normalizedPrefillData?.typesData || {};
    const selectedTypesRaw = read_array_fallback(
        normalizedPrefillData?.selectedTypes,
        prefillTypesData?.selectedTypes,
        defaultOfferPayload.selectedTypes
    );
    const setupData = normalizedPrefillData?.setupData || defaultOfferPayload.setupData;
    const selectedTypes = normalize_selected_website_types(selectedTypesRaw, setupData);
    const clientEmails = read_array_fallback(normalizedPrefillData?.clientEmails, prefillTypesData?.clientEmails);
    const prefillClient = normalizedPrefillData?.client || {};
    const category = normalize_industry_value(read_string_fallback(
        prefillTypesData?.category,
        prefillTypesData?.Category,
        prefillTypesData?.sourceClientCategory,
        prefillTypesData?.businessType,
        prefillTypesData?.business_type,
        prefillTypesData?.industry,
        prefillClient?.category,
        prefillClient?.Category,
        prefillClient?.businessType,
        prefillClient?.business_type,
        prefillClient?.industry,
        normalizedPrefillData?.category,
        normalizedPrefillData?.Category,
        normalizedPrefillData?.businessType,
        normalizedPrefillData?.business_type,
        normalizedPrefillData?.industry
    ));
    const subcategory = normalize_industry_value(read_string_fallback(
        prefillTypesData?.subcategory,
        prefillTypesData?.Subcategory,
        prefillTypesData?.sourceClientSubcategory,
        prefillTypesData?.subCategory,
        prefillTypesData?.sector,
        prefillClient?.subcategory,
        prefillClient?.Subcategory,
        prefillClient?.subCategory,
        prefillClient?.sector,
        prefillClient?.industry,
        normalizedPrefillData?.subcategory,
        normalizedPrefillData?.Subcategory,
        normalizedPrefillData?.subCategory,
        normalizedPrefillData?.sector
    ));
    const clientName = read_string_fallback(
        prefillTypesData?.clientName,
        normalizedPrefillData?.clientName,
        prefillClient?.brand,
        prefillClient?.brandName,
        prefillClient?.name
    );
    const clientHeadquarters = read_string_fallback(
        prefillTypesData?.clientHeadquarters,
        normalizedPrefillData?.clientHeadquarters,
        prefillClient?.location
    );
    const clientVatNumber = read_string_fallback(
        prefillTypesData?.clientVatNumber,
        normalizedPrefillData?.clientVatNumber,
        prefillClient?.vat
    );
    const offerLanguage = read_prefill_offer_language(normalizedPrefillData);
    const offerCode = read_prefill_is_niffler(prefillTypesData)
        ? ''
        : read_string_fallback(prefillTypesData?.offerCode, normalizedPrefillData?.code);

    return {
        ...defaultOfferPayload,
        selectedTypes,
        clientEmails,
        category,
        subcategory,
        industry: category,
        clientName,
        clientHeadquarters,
        clientVatNumber,
        offerLanguage,
        code: offerCode,
        typesData: {
            ...defaultOfferPayload.typesData,
            ...prefillTypesData,
            selectedTypes,
            clientEmails,
            category,
            subcategory,
            industry: category,
            clientName,
            clientHeadquarters,
            clientVatNumber,
            offerLanguage,
            invoiceEnabled: prefillTypesData?.invoiceEnabled === false ? false : true,
            offerCode,
            requestOfferMode: false
        },
        setupData,
        priceData: normalizedPrefillData?.priceData || defaultOfferPayload.priceData,
        niffler: read_object_fallback(normalizedPrefillData?.niffler, defaultOfferPayload.niffler),
        status: 'draft',
        active: true
    };
};

const canonical_create_offer_payload = (offerPayload = {}) => {
    const typesData = offerPayload?.typesData || {};
    const selectedType = offerPayload?.selectedType || offerPayload?.selectedTypes?.[0] || typesData?.selectedTypes?.[0] || '';
    const clientEmails = read_array_fallback(offerPayload?.client?.emails, offerPayload?.clientEmails, typesData?.clientEmails);
    const contactPhones = read_array_fallback(offerPayload?.client?.numbers, typesData?.contactPhones);
    const category = normalize_industry_value(read_string_fallback(
        typesData?.category,
        typesData?.Category,
        typesData?.sourceClientCategory,
        typesData?.businessType,
        typesData?.business_type,
        typesData?.industry,
        offerPayload?.category,
        offerPayload?.client?.category,
        offerPayload?.client?.Category,
        offerPayload?.client?.businessType,
        offerPayload?.client?.business_type,
        offerPayload?.client?.industry,
        offerPayload?.industry
    ));
    const subcategory = normalize_industry_value(read_string_fallback(
        typesData?.subcategory,
        typesData?.Subcategory,
        typesData?.sourceClientSubcategory,
        typesData?.subCategory,
        typesData?.sector,
        offerPayload?.subcategory,
        offerPayload?.client?.subcategory,
        offerPayload?.client?.Subcategory,
        offerPayload?.client?.subCategory,
        offerPayload?.client?.sector,
        offerPayload?.client?.industry
    ));
    const sourceSystem = read_string_fallback(typesData?.sourceSystem, offerPayload?.niffler?.sourceSystem);
    const sourceType = read_normalized_value(typesData?.sourceNifflerAction) === 'sass' || selectedType === 'sass'
        ? 'sass'
        : read_normalized_value(sourceSystem).startsWith('niffler')
            ? 'agency'
            : '';

    return {
        ofrId: offerPayload?.ofrId || typesData?.offerCode || '',
        selectedType,
        language: offerPayload?.language || offerPayload?.offerLanguage || typesData?.offerLanguage || 'el',
        invoice: offerPayload?.invoice === false || typesData?.invoiceEnabled === false ? false : true,
        submittedBy: offerPayload?.submittedBy || '',
        reviewerUserId: offerPayload?.setupData?.reviewerUserId || '',
        ...(typeof offerPayload.reviewEnabled === 'boolean' ? { reviewEnabled: offerPayload.reviewEnabled } : {}),
        selfReviewConfirmed: offerPayload.selfReviewConfirmed === true,
        deleted: offerPayload?.deleted === true,
        typesData: {
            ...typesData
        },
        client: {
            brand: offerPayload?.client?.brand || typesData?.clientName || offerPayload?.clientName || '',
            emails: clientEmails,
            numbers: contactPhones,
            category,
            subcategory,
            ownerFirstname: offerPayload?.client?.ownerFirstname || typesData?.sourceClientFirstname || typesData?.clientName || '',
            ownerLastname: offerPayload?.client?.ownerLastname || typesData?.sourceClientLastname || '',
            location: offerPayload?.client?.location || typesData?.clientHeadquarters || offerPayload?.clientHeadquarters || '',
            vat: offerPayload?.client?.vat || typesData?.clientVatNumber || offerPayload?.clientVatNumber || typesData?.sourceClientVat || ''
        },
        setupData: {
            ...(offerPayload?.setupData || {}),
            requestOfferMode: typesData?.requestOfferMode === true || offerPayload?.setupData?.requestOfferMode === true,
            preofferEnabled: typesData?.preofferEnabled === true || offerPayload?.setupData?.preofferEnabled === true,
            preofferAssignedTeamLead: typesData?.preofferAssignedTeamLead || offerPayload?.setupData?.preofferAssignedTeamLead || null,
            preofferRequestedAt: typesData?.preofferRequestedAt || offerPayload?.setupData?.preofferRequestedAt || '',
            preofferRequestedBy: typesData?.preofferRequestedBy || offerPayload?.setupData?.preofferRequestedBy || null,
            preofferTurn: typesData?.preofferTurn || offerPayload?.setupData?.preofferTurn || '',
            preofferEstimateSubmittedAt: typesData?.preofferEstimateSubmittedAt || offerPayload?.setupData?.preofferEstimateSubmittedAt || '',
            preofferEstimateSubmittedBy: typesData?.preofferEstimateSubmittedBy || offerPayload?.setupData?.preofferEstimateSubmittedBy || null,
            preofferDepartmentBoard: typesData?.preofferDepartmentBoard || offerPayload?.setupData?.preofferDepartmentBoard || ''
        },
        priceData: offerPayload?.priceData || {},
        payments: offerPayload?.payments || {},
        statusline: offerPayload?.statusline || [
            {
                status: 'draft',
                at: ''
            }
        ],
        activity: offerPayload?.activity || {
            visits: [],
            actionLog: []
        },
        content: offerPayload?.content || {
            snapshot: {},
            lockedAt: '',
            template: {
                key: '',
                fingerprint: '',
                version: {}
            }
        },
        niffler: {
            ...(offerPayload?.niffler || {}),
            sourceType,
            sourceSystem,
            sourceProspectId: typesData?.sourceProspectId || offerPayload?.niffler?.sourceProspectId || '',
            sourceBrandId: typesData?.sourceBrandId || offerPayload?.niffler?.sourceBrandId || '',
            sourceLeadId: typesData?.sourceLeadId || offerPayload?.niffler?.sourceLeadId || '',
            action: typesData?.sourceNifflerAction || offerPayload?.niffler?.action || '',
            meetingDate: typesData?.sassMeetingDate || typesData?.meetingDate || offerPayload?.niffler?.meetingDate || '',
            seller: typesData?.sassSeller || offerPayload?.niffler?.seller || null,
            caller: typesData?.sassCaller || offerPayload?.niffler?.caller || null,
            timeline: offerPayload?.niffler?.timeline || typesData?.sassTimeline || []
        }
    };
};

const scroll_page_top = () => {
    window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
    });
};

const notify_niffler_offer_completed = (offerData = {}) => {
    const nifflerSync = offerData?.nifflerSync || {};

    if (!nifflerSync?.synced || !window.opener) {
        return;
    }

    window.opener.postMessage({
        type: 'niffler_offer_completed',
        lead: nifflerSync?.lead || null,
        prospect: nifflerSync?.prospect || null,
        offer: offerData,
        ofrid: nifflerSync?.ofrid || ''
    }, '*');
};

const read_offer_total_visits = (offerDraft = {}) => {
    return offer_visit_count(offerDraft);
};

const read_main_loader_text = (loadingState = {}, t) => {
    if (loadingState?.isDraftSaving) {
        return t('Saving draft...');
    }

    if (loadingState?.handoffIsWaiting) {
        return t('Receiving CRM offer data...');
    }

    return t('Loading draft...');
};

export default function NEW_OFFER() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user.user || {});
    const [searchParams] = useSearchParams();
    const { isReady: hrAccessIsReady, member, permissions } = useHrAccess();
    const prefillInitKeyRef = useRef('');
    const acceptedHandoffIdRef = useRef('');
    const draftActionInProgressRef = useRef(false);
    const routeLanguage = get_route_language(i18n.language);
    const queryOfferId = `${searchParams.get('offerId') || ''}`.trim();
    const queryPrefillValue = `${searchParams.get('prefill') || ''}`.trim();
    const queryHandoffId = `${searchParams.get('handoffId') || ''}`.trim();
    const queryHandoffValue = read_normalized_value(searchParams.get('handoff') || '');
    const queryContinueOfferValue = read_normalized_value(searchParams.get('continueOffer') || '');
    const queryPreofferIssueId = `${searchParams.get('issueId') || ''}`.trim();
    const queryPreofferStage = read_normalized_value(searchParams.get('stage') || '');
    const queryHasNifflerHandoff = queryHandoffValue === 'niffler' && queryHandoffId.length > 0;
    const queryWantsContinueOffer = queryContinueOfferValue === '1' || queryContinueOfferValue === 'true';
    const continueMode = queryOfferId.length > 0;
    const [fakeOffer, setFakeOffer] = useState(() => searchParams.get('fake') === '1'
        || (!queryOfferId && !queryPrefillValue && !queryHandoffId && read_stored_offer_draft()?.fakeOffer === true));
    const [step, setStep] = useState(0);
    const [setupTypes, setSetupTypes] = useState([]);
    const [typesData, setTypesData] = useState({});
    const [setupData, setSetupData] = useState({});
    const [priceData, setPriceData] = useState({});
    const [offerId, setOfferId] = useState('');
    const [offerCreatedAt, setOfferCreatedAt] = useState('');
    const [isDraftLoading, setIsDraftLoading] = useState(true);
    const [isDraftSaving, setIsDraftSaving] = useState(false);
    const [draftError, setDraftError] = useState('');
    const [preofferSessionError, setPreofferSessionError] = useState(false);
    const [pendingSyncOffer, setPendingSyncOffer] = useState(null);
    const [handoffPrefillData, setHandoffPrefillData] = useState(null);
    const [handoffHasResolved, setHandoffHasResolved] = useState(!queryHandoffId.length);
    const [currentOfferDraft, setCurrentOfferDraft] = useState(null);
    const [offerApiMode, setOfferApiMode] = useState('agency');
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [adminActionType, setAdminActionType] = useState('');
    const submissionInProgress = useRef(false);
    const handoffIsWaiting = queryHasNifflerHandoff && !handoffHasResolved && !draftError.length;
    const mainLoaderIsVisible = new_offer_main_loader_is_visible({
        handoffIsWaiting,
        isDraftLoading,
        isDraftSaving,
        hasDraftError: Boolean(draftError.length)
    });
    const mainLoaderText = read_main_loader_text({
        handoffIsWaiting,
        isDraftSaving
    }, t);
    const draftHasFatalLoadError = preofferSessionError || (continueMode
        && !isDraftLoading
        && !currentOfferDraft
        && Boolean(draftError.length));
    const requestOfferSourceMode = continueMode
        && agencyOfferWorkflowsEnabled
        && request_offer_setup_mode(false, typesData);
    const legacyPreofferIssueReview = legacy_preoffer_issue_review_state(currentOfferDraft || {
        ofrId: typesData?.offerCode,
        typesData,
        setupData
    });
    const offerWorkflowMode = legacyPreofferIssueReview.workflowMode;
    const preofferV2Mode = Number(currentOfferDraft?.workflow?.version || currentOfferDraft?.setupData?.preofferWorkflowVersion || 0) === 2;
    const preofferV2Stage = read_normalized_value(currentOfferDraft?.workflow?.turn || queryPreofferStage);
    const freshAgencyHelpMode = agencyOfferWorkflowsEnabled
        && read_prefill_is_niffler(typesData)
        && (!continueMode || currentOfferDraft?.legacyOffer === true);
    const departmentManagerEstimateMode = ['help_department', 'request_department'].includes(offerWorkflowMode);
    const growthContinuationMode = ['help_growth', 'request_growth'].includes(offerWorkflowMode);
    const requestGrowthContinuationMode = offerWorkflowMode === 'request_growth';
    const lastStep = 3;
    const currentMemberIdentityKeys = read_member_identity_keys(member || {});
    const assignedTeamLeadIdentityKeys = read_preoffer_assigned_identity_keys(typesData || {});
    const isAssignedPreofferTeamLead = assignedTeamLeadIdentityKeys.some((identityKey) => {
        return currentMemberIdentityKeys.includes(identityKey);
    });
    const canContinueDepartmentManagerReview = permissions.canContinuePreofferRequests
        || isAssignedPreofferTeamLead;
    const coordinatorIdentityKeys = read_member_identity_keys(typesData?.preofferRequestedBy || {});
    const isCoordinatorTurnMember = coordinatorIdentityKeys.some((identityKey) => {
        return currentMemberIdentityKeys.includes(identityKey);
    });
    const canContinueGrowth = requestGrowthContinuationMode
        ? permissions.isGrowthDepartmentMember
        : preoffer_coordinator_can_continue(permissions, isCoordinatorTurnMember);
    const activeOfferReadOnly = currentOfferDraft?.setupData?.serviceActivation?.state === 'active'
        || (!departmentManagerEstimateMode && !requestGrowthContinuationMode && (!currentOfferDraft?.workflow?.turn || currentOfferDraft.workflow.turn === 'done')
            && (currentOfferDraft?.status === 'paid' || currentOfferDraft?.payments?.recurring?.status === 'paid' || currentOfferDraft?.payments?.oneOff?.status === 'paid'));
    const offerWorkflowReadOnly = activeOfferReadOnly || (departmentManagerEstimateMode && !canContinueDepartmentManagerReview)
        || (growthContinuationMode && !canContinueGrowth);
    const preofferTurn = read_preoffer_turn(typesData || {});
    const isPreofferRequested = read_is_preoffer_requested(typesData || {});
    const lockSetupStepZero = (isPreofferRequested || departmentManagerEstimateMode)
        && preoffer_department_estimate_turn(offerWorkflowMode, preofferTurn)
        && canContinueDepartmentManagerReview;
    const canShowDeleteButton = continueMode
        && !isDraftLoading
        && checkPermissions('Baron', user?.roles)
        && Boolean(currentOfferDraft?.offerId)
        && read_offer_total_visits(currentOfferDraft) === 0;

    const hydrate_offer_draft = (offerDraft, initialStep = 0) => {
        if (Object.hasOwn(offerDraft || {}, 'fakeOffer')) setFakeOffer(offerDraft.fakeOffer === true);
        const draftTypesData = offerDraft?.typesData || {};
        const draftSetupData = offerDraft?.setupData || {};
        const draftPriceData = offerDraft?.priceData || {};
        const workflowMode = offer_workflow_mode(offerDraft || {});
        const helpWorkflow = workflowMode.startsWith('help_');
        const normalizedSetupData = normalize_setup_data_for_legacy(draftSetupData, draftPriceData, offerDraft || {});
        const selectedTypesFromDraft = offerDraft?.selectedType
            ? [offerDraft.selectedType]
            : Array.isArray(offerDraft?.selectedTypes) ? offerDraft.selectedTypes : [];
        const selectedTypesFromTypesData = Array.isArray(draftTypesData?.selectedTypes) ? draftTypesData.selectedTypes : [];
        const selectedTypesFromSetupData = infer_selected_types_from_setup_data(normalizedSetupData);
        const defaultSelectedTypes = fresh_offer_selected_types(
            draftTypesData?.requestOfferMode === true || draftSetupData?.requestOfferMode === true
        );
        const rawSelectedTypes = selectedTypesFromDraft.length
            ? selectedTypesFromDraft
            : selectedTypesFromTypesData.length
                ? selectedTypesFromTypesData
                : selectedTypesFromSetupData.length
                    ? selectedTypesFromSetupData
                    : defaultSelectedTypes;
        const selectedTypes = normalize_selected_website_types(rawSelectedTypes, normalizedSetupData);
        const normalizedCategoryValue = normalize_industry_value(
            read_string_fallback(
                draftTypesData?.category,
                draftTypesData?.Category,
                draftTypesData?.sourceClientCategory,
                draftTypesData?.businessType,
                draftTypesData?.business_type,
                draftTypesData?.industry,
                offerDraft?.category,
                offerDraft?.client?.category,
                offerDraft?.client?.Category,
                offerDraft?.client?.businessType,
                offerDraft?.client?.business_type,
                offerDraft?.client?.industry,
                offerDraft?.industry
            )
        );
        const normalizedSubcategoryValue = normalize_industry_value(
            read_string_fallback(
                draftTypesData?.subcategory,
                draftTypesData?.Subcategory,
                draftTypesData?.sourceClientSubcategory,
                draftTypesData?.subCategory,
                draftTypesData?.sector,
                offerDraft?.subcategory,
                offerDraft?.client?.subcategory,
                offerDraft?.client?.Subcategory,
                offerDraft?.client?.subCategory,
                offerDraft?.client?.sector,
                offerDraft?.client?.industry
            )
        );
        const normalizedTypesData = {
            ...draftTypesData,
            selectedTypes,
            clientEmails: read_array_fallback(draftTypesData?.clientEmails, offerDraft?.clientEmails, offerDraft?.client?.emails),
            clientName: read_string_fallback(
                draftTypesData?.clientName,
                offerDraft?.clientName,
                `${offerDraft?.client?.ownerFirstname || ''} ${offerDraft?.client?.ownerLastname || ''}`.trim()
            ),
            clientHeadquarters: read_string_fallback(draftTypesData?.clientHeadquarters, offerDraft?.clientHeadquarters, offerDraft?.client?.location),
            clientVatNumber: read_string_fallback(draftTypesData?.clientVatNumber, offerDraft?.clientVatNumber, offerDraft?.client?.vat),
            offerCode: read_string_fallback(draftTypesData?.offerCode, offerDraft?.ofrId, offerDraft?.code),
            offerLanguage: read_string_fallback(draftTypesData?.offerLanguage, offerDraft?.language, offerDraft?.offerLanguage, 'el'),
            invoiceEnabled: offerDraft?.invoice === false || draftTypesData?.invoiceEnabled === false ? false : true,
            requestOfferMode: draftTypesData?.requestOfferMode === true || draftSetupData?.requestOfferMode === true,
            preofferWorkflowVersion: Number(offerDraft?.workflow?.version || draftSetupData?.preofferWorkflowVersion || 0),
            preofferEnabled: Boolean(read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferEnabled')) || helpWorkflow,
            preofferAssignedTeamLead: read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferAssignedTeamLead')
                || offerDraft?.workflow?.assignedTeamLead
                || null,
            preofferRequestedAt: read_string_fallback(
                read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferRequestedAt'),
                offerDraft?.workflow?.requestedAt,
                helpWorkflow ? offerDraft?.createdAt : ''
            ),
            preofferRequestedBy: read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferRequestedBy')
                || offerDraft?.workflow?.requestedBy
                || null,
            preofferTurn: read_string_fallback(
                read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferTurn'),
                offerDraft?.workflow?.turn,
                workflowMode === 'help_department' ? 'team_lead' : '',
                workflowMode === 'help_growth' ? 'coordinator' : ''
            ),
            preofferEstimateSubmittedAt: read_string_fallback(
                read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferEstimateSubmittedAt'),
                offerDraft?.workflow?.estimateSubmittedAt
            ),
            preofferEstimateSubmittedBy: read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferEstimateSubmittedBy')
                || offerDraft?.workflow?.estimateSubmittedBy
                || null,
            preofferDepartmentBoard: read_string_fallback(
                read_value_from_scopes(draftTypesData, draftSetupData, offerDraft, 'preofferDepartmentBoard'),
                offerDraft?.workflow?.preofferDepartmentBoard,
                preoffer_department_board_from_types(selectedTypes)
            ),
            category: normalizedCategoryValue,
            subcategory: normalizedSubcategoryValue,
            industry: normalizedCategoryValue
        };

        setStep(initialStep);
        setSetupTypes(selectedTypes);
        setTypesData(normalizedTypesData);
        setSetupData(normalizedSetupData);
        setPriceData(offerDraft?.fakeOffer ? draftPriceData : merge_price_data_with_setup(draftPriceData, normalizedSetupData));
        setOfferCreatedAt(`${offerDraft?.createdAt || ''}`.trim());
        setCurrentOfferDraft(offerDraft || null);
    };

    const persist_offer_draft = async(nextStep, nextTypesData, nextSetupData, nextPriceData, nextSelectedTypes, statusOverride = '') => {
        if (draftActionInProgressRef.current) {
            return false;
        }

        draftActionInProgressRef.current = true;
        setIsDraftSaving(true);
        setDraftError('');

        try {
            const normalizedCategoryValue = normalize_industry_value(read_string_fallback(
                nextTypesData?.category,
                nextTypesData?.Category,
                nextTypesData?.sourceClientCategory,
                nextTypesData?.businessType,
                nextTypesData?.business_type,
                nextTypesData?.industry
            ));
            const normalizedSubcategoryValue = normalize_industry_value(read_string_fallback(
                nextTypesData?.subcategory,
                nextTypesData?.Subcategory,
                nextTypesData?.sourceClientSubcategory,
                nextTypesData?.subCategory,
                nextTypesData?.sector
            ));
            const normalizedTypesData = {
                ...nextTypesData,
                category: normalizedCategoryValue,
                subcategory: normalizedSubcategoryValue,
                industry: normalizedCategoryValue,
                preofferDepartmentBoard: nextTypesData?.preofferDepartmentBoard
                    || nextSetupData?.preofferDepartmentBoard
                    || preoffer_department_board_from_types(first_selected_types(nextTypesData?.selectedTypes, nextSelectedTypes, setupTypes))
            };
            const selectedType = nextSelectedTypes?.[0] || '';
            const nextStatus = statusOverride.length ? statusOverride : (nextStep >= lastStep ? 'ready_for_review' : 'draft');
            const preofferTurn = `${normalizedTypesData?.preofferTurn || ''}`.trim();
            const preofferRequestedAt = `${normalizedTypesData?.preofferRequestedAt || ''}`.trim();
            const nextStatuslineStatus = preofferTurn === 'team_lead'
                ? 'help'
                : preofferRequestedAt.length || nextStatus === 'ready_for_review'
                    ? 'requested'
                    : 'draft';
            const existingStatusline = Array.isArray(currentOfferDraft?.statusline) && currentOfferDraft.statusline.length
                ? currentOfferDraft.statusline
                : [];
            const nextStatusline = existingStatusline[existingStatusline.length - 1]?.status === nextStatuslineStatus
                ? existingStatusline
                : [
                    ...existingStatusline,
                    {
                        status: nextStatuslineStatus,
                        at: new Date().toISOString()
                    }
                ];
            const submittedBy = `${user?.subId || user?.sub || user?.userId || user?.id || user?._id || ''}`.trim();
            const canonicalSetupData = {
                ...nextSetupData,
                requestOfferMode: normalizedTypesData?.requestOfferMode || nextSetupData?.requestOfferMode || false,
                preofferEnabled: Boolean(normalizedTypesData?.preofferEnabled || nextSetupData?.preofferEnabled),
                preofferAssignedTeamLead: normalizedTypesData?.preofferAssignedTeamLead || nextSetupData?.preofferAssignedTeamLead || null,
                preofferRequestedAt: normalizedTypesData?.preofferRequestedAt || nextSetupData?.preofferRequestedAt || '',
                preofferRequestedBy: normalizedTypesData?.preofferRequestedBy || nextSetupData?.preofferRequestedBy || null,
                preofferTurn: normalizedTypesData?.preofferTurn || nextSetupData?.preofferTurn || '',
                preofferEstimateSubmittedAt: normalizedTypesData?.preofferEstimateSubmittedAt || nextSetupData?.preofferEstimateSubmittedAt || '',
                preofferEstimateSubmittedBy: normalizedTypesData?.preofferEstimateSubmittedBy || nextSetupData?.preofferEstimateSubmittedBy || null,
                preofferDepartmentBoard: normalizedTypesData?.preofferDepartmentBoard || nextSetupData?.preofferDepartmentBoard || ''
            };

            const updatedOfferDraft = {
                ...(currentOfferDraft || {}),
                step: nextStep,
                offerId,
                _id: currentOfferDraft?._id || offerId || '',
                ofrId: normalizedTypesData?.offerCode || '',
                selectedType,
                language: normalizedTypesData?.offerLanguage || 'el',
                invoice: normalizedTypesData?.invoiceEnabled === false ? false : true,
                submittedBy,
                deleted: false,
                client: {
                    brand: normalizedTypesData?.clientName || '',
                    emails: normalizedTypesData?.clientEmails || [],
                    numbers: [
                        normalizedTypesData?.contactPhone || '',
                        ...(Array.isArray(normalizedTypesData?.contactPhones) ? normalizedTypesData.contactPhones : [])
                    ].filter(Boolean),
                    category: normalizedCategoryValue,
                    subcategory: normalizedSubcategoryValue,
                    ownerFirstname: normalizedTypesData?.sourceClientFirstname || normalizedTypesData?.clientName || '',
                    ownerLastname: normalizedTypesData?.sourceClientLastname || '',
                    location: normalizedTypesData?.clientHeadquarters || '',
                    vat: normalizedTypesData?.clientVatNumber || normalizedTypesData?.sourceClientVat || ''
                },
                typesData: normalizedTypesData,
                setupData: canonicalSetupData,
                priceData: nextPriceData,
                statusline: nextStatusline,
                activity: {
                    ...(currentOfferDraft?.activity || {}),
                    visits: Array.isArray(currentOfferDraft?.activity?.visits)
                        ? currentOfferDraft.activity.visits
                        : Array.isArray(currentOfferDraft?.visits) ? currentOfferDraft.visits : [],
                    actionLog: Array.isArray(currentOfferDraft?.activity?.actionLog)
                        ? currentOfferDraft.activity.actionLog
                        : Array.isArray(currentOfferDraft?.offerUpdates) ? currentOfferDraft.offerUpdates : []
                },
                niffler: currentOfferDraft?.niffler || {}
            };

            let savedOfferDraft = updatedOfferDraft;

            if (offerApiMode === 'legacy') {
                const { fakeOffer: localFakeIntent, ...draftPayload } = updatedOfferDraft;
                const response = offerId
                    ? await update_offer_draft_by_id(offerId, draftPayload)
                    : await create_offer_draft(draftPayload);

                savedOfferDraft = {
                    ...(response?.data?.data || updatedOfferDraft),
                    legacyOffer: true
                };
            }

            if (savedOfferDraft?.offerId && !offerId) {
                setOfferId(savedOfferDraft.offerId);
                write_stored_offer_id(savedOfferDraft.offerId);
            }

            savedOfferDraft = { ...savedOfferDraft, fakeOffer };
            write_stored_offer_draft(savedOfferDraft);
            setCurrentOfferDraft(savedOfferDraft);
            return savedOfferDraft;
        } catch (error) {
            setDraftError('Could not save offer draft.');
            return false;
        } finally {
            draftActionInProgressRef.current = false;
            setIsDraftSaving(false);
        }
    };

    useEffect(() => {
        if (!queryHandoffId.length) {
            acceptedHandoffIdRef.current = '';
            setHandoffPrefillData(null);
            setHandoffHasResolved(true);
            return;
        }

        let waitTimer = null;

        acceptedHandoffIdRef.current = '';
        setIsDraftLoading(true);
        setHandoffPrefillData(null);
        setHandoffHasResolved(false);

        const receive_offer_prefill = (event) => {
            const message = event?.data || {};

            if (!handoff_prefill_should_apply(message, queryHandoffId, acceptedHandoffIdRef.current)) {
                return;
            }

            acceptedHandoffIdRef.current = `${message?.handoffId || ''}`.trim();
            window.clearTimeout(waitTimer);
            setDraftError('');
            setHandoffPrefillData(message?.prefill || null);
            setHandoffHasResolved(true);
        };

        window.addEventListener('message', receive_offer_prefill);
        waitTimer = window.setTimeout(() => {
            setDraftError('Could not receive CRM offer data.');
            setIsDraftLoading(false);
        }, offerHandoffTimeoutMs);

        return () => {
            window.clearTimeout(waitTimer);
            window.removeEventListener('message', receive_offer_prefill);
        };
    }, [queryHandoffId]);

    useEffect(() => {
        let mounted = true;

        const init_offer_draft = async() => {
            if (!hrAccessIsReady || !handoffHasResolved) {
                setIsDraftLoading(true);
                return;
            }

            if (!permissions.isAcceptedMember) {
                setIsDraftLoading(false);
                navigate(`/${routeLanguage}/access-denied`, { replace: true });
                return;
            }

            setIsDraftLoading(true);
            setDraftError('');
            setPreofferSessionError(false);
            setPendingSyncOffer(null);
            setCurrentOfferDraft(null);

            let offerDraft = null;
            let hasValidatedTaskSession = false;
            const requestedOfferId = `${queryOfferId || ''}`.trim();
            const storedOfferDraft = read_stored_offer_draft();
            if (storedOfferDraft?.cerebroSyncPending === true
                && offer_uses_cerebro_handoff(storedOfferDraft)
                && storedOfferDraft.offerId === requestedOfferId
                && !queryPreofferIssueId) {
                setPendingSyncOffer(storedOfferDraft);
                setOfferId(storedOfferDraft.offerId);
                setIsDraftLoading(false);
                return;
            }
            const prefillPayload = handoffPrefillData || read_query_offer_prefill(queryPrefillValue);
            const storedDraftIsRequest = request_offer_setup_mode(false, storedOfferDraft?.typesData || {})
                || storedOfferDraft?.setupData?.requestOfferMode === true;
            const storedDraftIsNiffler = read_prefill_is_niffler(storedOfferDraft?.typesData || {});
            const storedDraftCanResume = Boolean(storedOfferDraft && stored_offer_can_resume(storedOfferDraft))
                && storedDraftIsNiffler && !storedDraftIsRequest;
            const canStartFreshAgencyOffer = Boolean(prefillPayload)
                && prefill_has_niffler_agency_identity(prefillPayload);

            if (!requestedOfferId.length
                && !canStartFreshAgencyOffer
                && !storedDraftCanResume) {
                setIsDraftLoading(false);
                navigate(`/${routeLanguage}/access-denied`, { replace: true });
                return;
            }
            const prefillInitKey = read_prefill_init_key(queryHandoffId, queryPrefillValue);
            const prefillWasInitialized = prefillInitKey.length > 0 && prefillInitKeyRef.current === prefillInitKey;
            const shouldCreateFreshPrefillDraft = Boolean(prefillPayload) && !requestedOfferId.length && !prefillWasInitialized;
            const shouldIgnoreStoredDraft = (queryPrefillValue.length > 0 || queryHandoffId.length > 0)
                && !requestedOfferId.length
                && !prefillWasInitialized;

            if (queryHasNifflerHandoff && !prefillPayload) {
                setDraftError('Could not receive CRM offer data.');
                setIsDraftLoading(false);
                return;
            }

            if (shouldCreateFreshPrefillDraft) {
                clear_offer_related_browser_state();
            }

            if (shouldCreateFreshPrefillDraft && prefill_is_sass_import(prefillPayload)) {
                setDraftError('SASS offers are created from Leads.');
                setIsDraftLoading(false);
                return;
            }

            if (requestedOfferId.length && queryPreofferIssueId.length) {
                try {
                    const sessionResponse = await get_agency_preoffer_session(requestedOfferId, queryPreofferIssueId);
                    const sessionOffer = sessionResponse?.data?.data?.offer || null;

                    if (sessionOffer?.offerId) {
                        offerDraft = sessionOffer;
                        hasValidatedTaskSession = true;
                        setOfferApiMode('agency');
                    } else {
                        setDraftError(t('Could not validate the Cerebro Preoffer session.'));
                        setPreofferSessionError(true);
                        setIsDraftLoading(false);
                        return;
                    }
                } catch (error) {
                    if (preoffer_session_uses_legacy_workflow(error)) {
                        try {
                            const requestedDraftResponse = await get_agency_offer_by_id(requestedOfferId);
                            const requestedOfferDraft = requestedDraftResponse?.data?.data || null;

                            if (requestedOfferDraft?.offerId) {
                                offerDraft = {
                                    ...requestedOfferDraft,
                                    legacyPreofferIssueContinuation: true
                                };
                                setOfferApiMode('agency');
                            }
                        } catch (legacyError) {
                            offerDraft = null;
                        }
                    } else {
                        setDraftError(error?.response?.data?.message || 'Could not validate the Cerebro Preoffer session.');
                        setPreofferSessionError(true);
                        setIsDraftLoading(false);
                        return;
                    }
                }
            }

            if (requestedOfferId.length && !offerDraft && !queryPreofferIssueId.length) {
                try {
                    const requestedDraftResponse = await get_agency_offer_by_id(requestedOfferId);
                    const requestedOfferDraft = requestedDraftResponse?.data?.data || null;

                    if (requestedOfferDraft?.offerId) {
                        offerDraft = requestedOfferDraft;
                        setOfferApiMode('agency');
                    }
                } catch (error) {
                    offerDraft = null;
                }
            }

            if (requestedOfferId.length && !offerDraft) {
                try {
                    const requestedDraftResponse = await get_offer_draft_by_id(requestedOfferId);
                    const requestedOfferDraft = requestedDraftResponse?.data?.data || null;

                    if (requestedOfferDraft?.offerId) {
                        offerDraft = {
                            ...requestedOfferDraft,
                            legacyOffer: true
                        };
                        setOfferApiMode('legacy');
                    }
                } catch (error) {
                    offerDraft = null;
                }
            }

            if (requestedOfferId.length && !offerDraft) {
                setDraftError('Could not load offer draft.');
                setIsDraftLoading(false);
                return;
            }

            if (!offerDraft && storedOfferDraft && !shouldIgnoreStoredDraft) {
                if (stored_offer_can_resume(storedOfferDraft)) {
                    offerDraft = storedOfferDraft;
                    setOfferApiMode(storedOfferDraft?.legacyOffer === true ? 'legacy' : 'agency');
                } else {
                    clear_offer_related_browser_state();
                }
            }

            if (!offerDraft) {
                offerDraft = shouldCreateFreshPrefillDraft
                    ? create_prefilled_offer_payload(prefillPayload)
                    : create_default_offer_payload();
                setOfferApiMode('agency');
                offerDraft = { ...offerDraft, fakeOffer: searchParams.get('fake') === '1' };
                write_stored_offer_draft(offerDraft);

                if (shouldCreateFreshPrefillDraft) {
                    prefillInitKeyRef.current = prefillInitKey;
                }
            }

            if (!mounted) {
                return;
            }

            if (offer_uses_cerebro_handoff(offerDraft) && !hasValidatedTaskSession) {
                if (offerDraft.cerebroSyncPending === true) {
                    setPendingSyncOffer(offerDraft);
                    setOfferId(offerDraft.offerId);
                } else {
                    setDraftError(t('Open this offer from its In Progress task in Tasks.'));
                    setPreofferSessionError(true);
                }
                setIsDraftLoading(false);
                return;
            }

            if (offerDraft?.offerId) {
                setOfferId(offerDraft.offerId);
                write_stored_offer_id(offerDraft.offerId);

                if (shouldCreateFreshPrefillDraft) {
                    navigate(`/${routeLanguage}/new-offer?offerId=${encodeURIComponent(offerDraft.offerId)}`, {
                        replace: true
                    });
                }
            } else {
                setOfferId('');
                clear_stored_offer_id();
                write_stored_offer_draft(offerDraft);
            }

            const legacyIssueReview = legacy_preoffer_issue_review_state(offerDraft || {});

            hydrate_offer_draft(
                offerDraft || create_default_offer_payload(),
                legacyIssueReview.enabled
                    ? legacyIssueReview.startStep
                    : !requestedOfferId && storedDraftCanResume
                        ? Math.min(offerDraft.step || 0, 3)
                        : continue_offer_start_step(queryWantsContinueOffer, offerDraft || {})
            );
            setIsDraftLoading(false);
        };

        init_offer_draft();

        return () => {
            mounted = false;
        };
    }, [hrAccessIsReady, permissions.hasFullAccess, permissions.canCreateOffers, permissions.isAcceptedMember, permissions.isGrowthDepartmentMember, handoffHasResolved, handoffPrefillData, queryOfferId, queryPrefillValue, queryHandoffId, queryHasNifflerHandoff, queryWantsContinueOffer, queryPreofferIssueId]);

    useEffect(() => {
        if (isDraftLoading) {
            return;
        }

        scroll_page_top();
    }, [step, isDraftLoading]);

    const clear_offer_draft_memory = () => {
        const defaultOfferPayload = create_default_offer_payload();
        setFakeOffer(false);

        clear_offer_related_browser_state();
        setOfferId('');
        setOfferCreatedAt('');
        setStep(0);
        setSetupTypes(defaultOfferPayload.selectedTypes || []);
        setTypesData(defaultOfferPayload.typesData || {});
        setSetupData(defaultOfferPayload.setupData || {});
        setPriceData(defaultOfferPayload.priceData || {});
        setDraftError('');
        setCurrentOfferDraft(defaultOfferPayload);
        setOfferApiMode('agency');
        setIsDeleteConfirmationOpen(false);
        setAdminActionType('');
    };

    const delete_current_offer = async() => {
        if (!canShowDeleteButton || adminActionType.length || !currentOfferDraft?.offerId) {
            return;
        }

        setDraftError('');
        setAdminActionType('delete');

        try {
            const deleteOfferRequest = offerApiMode === 'legacy'
                ? delete_offer_by_id
                : delete_agency_offer_by_id;

            await deleteOfferRequest(currentOfferDraft.offerId);
            clear_offer_draft_memory();
            navigate(`/${routeLanguage}`, {
                replace: true
            });
        } catch (error) {
            setDraftError(t('Could not delete offer.'));
        } finally {
            setIsDeleteConfirmationOpen(false);
            setAdminActionType('');
        }
    };

    const go_back = () => {
        if (isDraftLoading || isDraftSaving || draftActionInProgressRef.current) {
            return;
        }

        if (step === 0) {
            clear_offer_draft_memory();
            navigate(`/${routeLanguage}`, {
                replace: true
            });
            return;
        }

        if (step === 1 && lockSetupStepZero) {
            navigate(`/${routeLanguage}`);
            return;
        }

        const previousStep = step - 1;

        setStep(previousStep);

        persist_offer_draft(previousStep, typesData, setupData, priceData, setupTypes);
    };

    const go_step = async(nextStep) => {
        if (isDraftLoading || isDraftSaving || draftActionInProgressRef.current) {
            return;
        }

        if (offerWorkflowReadOnly || (departmentManagerEstimateMode && nextStep > 1)) {
            return;
        }

        if (nextStep === step || nextStep < 0 || nextStep > lastStep) {
            return;
        }

        if (lockSetupStepZero && step === 1 && nextStep === 0) {
            return;
        }

        const savedOfferDraft = await persist_offer_draft(nextStep, typesData, setupData, priceData, setupTypes);

        const savedNextStep = draft_save_next_step(savedOfferDraft, nextStep);

        if (savedNextStep === null) {
            return;
        }

        if (savedOfferDraft?.offerId) {
            hydrate_offer_draft(savedOfferDraft, savedNextStep);
            return;
        }

        setStep(savedNextStep);
    };

    const go_next = async(stepData) => {
        if (isDraftLoading || isDraftSaving || draftActionInProgressRef.current) {
            return;
        }

        if (offerWorkflowReadOnly || departmentManagerEstimateMode) {
            setDraftError(t('This offer can only be continued by its current workflow owner.'));
            return;
        }

        if (step >= lastStep) {
            return;
        }

        let nextSetupData = step === 1 ? (stepData || {}) : setupData;
        const nextTypesData = step === 0
            ? {
                ...typesData,
                ...(stepData || {}),
                selectedTypes: requestOfferSourceMode && continueMode && setupTypes.length ? setupTypes : (stepData?.selectedTypes || typesData?.selectedTypes || setupTypes),
                requestOfferMode: stepData?.requestOfferMode === true || (requestOfferSourceMode && continueMode)
            }
            : step === 1
                ? {
                    ...typesData,
                    requestOfferMode: typesData?.requestOfferMode || requestOfferSourceMode,
                    preofferEnabled: Boolean(nextSetupData?.preofferEnabled ?? read_is_preoffer_enabled(typesData)),
                    preofferAssignedTeamLead: nextSetupData?.preofferAssignedTeamLead ?? typesData?.preofferAssignedTeamLead ?? null,
                    preofferDepartmentBoard: nextSetupData?.preofferDepartmentBoard
                        || typesData?.preofferDepartmentBoard
                        || preoffer_department_board_from_types(first_selected_types(typesData?.selectedTypes, setupTypes))
                }
                : typesData;
        let nextPriceData = priceData;
        if (step === 2) nextPriceData = fakeOffer ? stepData : merge_price_data_with_setup({ ...priceData, ...(stepData || {}) }, setupData);
        else if (step === 1) nextPriceData = merge_price_data_with_setup(priceData, nextSetupData);
        const nextSelectedTypes = requestOfferSourceMode && continueMode && setupTypes.length
            ? setupTypes
            : nextTypesData?.selectedTypes || setupTypes;
        const nextStep = step + 1;
        if (step === 0 && JSON.stringify(nextSelectedTypes) !== JSON.stringify(setupTypes)) {
            nextSetupData = {}; nextPriceData = {};
        }

        setSetupTypes(nextSelectedTypes);
        setTypesData(nextTypesData);
        setSetupData(nextSetupData);
        setPriceData(nextPriceData);

        const savedOfferDraft = await persist_offer_draft(
            nextStep,
            nextTypesData,
            nextSetupData,
            nextPriceData,
            nextSelectedTypes
        );

        const savedNextStep = draft_save_next_step(savedOfferDraft, nextStep);

        if (savedNextStep === null) {
            return;
        }

        if (savedOfferDraft?.offerId) {
            hydrate_offer_draft(savedOfferDraft, savedNextStep);
            return;
        }

        setStep(savedNextStep);
    };

    const request_preoffer_help = async(stepData = {}) => {
        if (isDraftSaving) {
            return;
        }

        const nextSetupData = stepData || {};
        const nextPriceData = merge_price_data_with_setup(priceData, nextSetupData);
        const now = new Date().toISOString();
        const preofferDepartmentBoard = nextSetupData?.preofferDepartmentBoard
            || typesData?.preofferDepartmentBoard
            || preoffer_department_board_from_types(first_selected_types(typesData?.selectedTypes, setupTypes));
        const nextTypesData = {
            ...typesData,
            preofferEnabled: Boolean(nextSetupData?.preofferEnabled),
            preofferAssignedTeamLead: null,
            preofferRequestedAt: now,
            preofferRequestedBy: read_member_payload(member),
            preofferTurn: 'team_lead',
            preofferEstimateSubmittedAt: '',
            preofferEstimateSubmittedBy: null,
            preofferDepartmentBoard
        };
        const nextSelectedTypes = nextTypesData?.selectedTypes || setupTypes;

        setSetupTypes(nextSelectedTypes);
        setTypesData(nextTypesData);
        setSetupData(nextSetupData);
        setPriceData(nextPriceData);

        const savedOfferDraft = await persist_offer_draft(
            1,
            nextTypesData,
            nextSetupData,
            nextPriceData,
            nextSelectedTypes,
            'draft'
        );

        if (!savedOfferDraft) {
            return;
        }

        setIsDraftSaving(true);

        try {
            await create_agency_help_offer(canonical_create_offer_payload(savedOfferDraft));
        } catch (error) {
            setDraftError(error?.response?.data?.message || 'Could not request help.');
            setIsDraftSaving(false);
            return;
        }

        setIsDraftSaving(false);
        clear_offer_related_browser_state();
        navigate(`/${routeLanguage}`, {
            replace: true
        });
    };

    const submit_preoffer_estimate = async(stepData = {}) => {
        if (!offerId || isDraftSaving) {
            return;
        }

        const nextSetupData = stepData || {};
        const nextPriceData = merge_price_data_with_setup(priceData, nextSetupData);
        const now = new Date().toISOString();
        const preofferDepartmentBoard = nextSetupData?.preofferDepartmentBoard
            || typesData?.preofferDepartmentBoard
            || preoffer_department_board_from_types(first_selected_types(typesData?.selectedTypes, setupTypes));
        const nextTypesData = {
            ...typesData,
            preofferEnabled: true,
            preofferAssignedTeamLead: nextSetupData?.preofferAssignedTeamLead || typesData?.preofferAssignedTeamLead || null,
            preofferTurn: 'coordinator',
            preofferEstimateSubmittedAt: now,
            preofferEstimateSubmittedBy: read_member_payload(member),
            preofferDepartmentBoard
        };
        const nextSelectedTypes = nextTypesData?.selectedTypes || setupTypes;

        setSetupTypes(nextSelectedTypes);
        setTypesData(nextTypesData);
        setSetupData(nextSetupData);
        setPriceData(nextPriceData);

        const savedOfferDraft = await persist_offer_draft(
            1,
            nextTypesData,
            nextSetupData,
            nextPriceData,
            nextSelectedTypes,
            'draft'
        );

        if (!savedOfferDraft) {
            return;
        }

        setIsDraftSaving(true);

        try {
            await submit_agency_help_estimate_by_id(offerId, {
                setupData: help_estimate_setup_payload(nextSetupData),
                submittedBy: read_member_payload(member)
            });
        } catch (error) {
            setDraftError(error?.response?.data?.message || 'Could not submit estimate.');
            setIsDraftSaving(false);
            return;
        }

        setIsDraftSaving(false);
        clear_offer_related_browser_state();
        navigate(`/${routeLanguage}`, {
            replace: true
        });
    };

    const apply_reviewer_prices = () => {
        const servicePriceOverwriteValues = { ...setupData.servicePriceOverwriteValues };
        for (const [serviceKey, value] of Object.entries(setupData.serviceSuggestedPricingValues || {})) {
            if (setupData.selectedServices?.[serviceKey] && Number(value) > 0) {
                servicePriceOverwriteValues[serviceKey] = value;
            }
        }

        setSetupData({ ...setupData, servicePriceOverwriteValues });
        setPriceData({
            ...priceData,
            servicePriceOverwriteValues,
            serviceCatalogByType: [],
            typeDiscountValues: {},
            marketingTotalValue: null,
            restTypeTotalValues: {},
            mixedRecurringTotalValue: '',
            mixedOneOffTotalValue: '',
            mixedRecurringManualNetAmount: null,
            mixedOneOffManualNetAmount: null
        });
        setStep(2);
    };

    const create_and_send_offer = async(offerContentPayload = {}) => {
        if (isDraftSaving || submissionInProgress.current || pendingSyncOffer) {
            return;
        }

        if (offer_uses_cerebro_handoff(currentOfferDraft) && !queryPreofferIssueId) {
            setDraftError(t('Open this offer from its In Progress task in Tasks.'));
            setPreofferSessionError(true);
            return;
        }

        const optionalReviewCreation = freshAgencyHelpMode;
        const reviewEnabled = setupData.reviewEnabled === true;
        if (optionalReviewCreation && reviewEnabled && !setupData.reviewerUserId) {
            setDraftError(t('Select a reviewer before continuing.'));
            setStep(1);
            return;
        }

        if (offerWorkflowReadOnly || departmentManagerEstimateMode) {
            setDraftError(t('Department Manager review must finish before Sales can create and send this offer.'));
            return;
        }

        if (preofferV2Mode
            && preofferV2Stage === 'preparer'
            && !reviewer_suggested_pricing_is_complete(setupData?.selectedServices, setupData?.serviceSuggestedPricingValues)) {
            setDraftError(t('Fill a positive suggested price for every selected service before submitting.'));
            return;
        }

        if (!fakeOffer && !offer_total_is_positive(priceData)) {
            setDraftError(t('Offer total must be greater than 0.'));
            return;
        }

        const isSelfReviewer = setupData.reviewerIsSelf === true || [member?.userId, member?.subId, member?.id, user?.sub].filter(Boolean).includes(setupData.reviewerUserId);
        submissionInProgress.current = true;
        setIsDraftSaving(true);
        setDraftError('');

        try {
            const normalizedCategoryValue = normalize_industry_value(read_string_fallback(
                typesData?.category,
                typesData?.Category,
                typesData?.sourceClientCategory,
                typesData?.businessType,
                typesData?.business_type,
                typesData?.industry
            ));
            const normalizedSubcategoryValue = normalize_industry_value(read_string_fallback(
                typesData?.subcategory,
                typesData?.Subcategory,
                typesData?.sourceClientSubcategory,
                typesData?.subCategory,
                typesData?.sector
            ));
            const preofferIsRequested = read_is_preoffer_requested({
                typesData,
                setupData
            });
            const normalizedTypesData = {
                ...typesData,
                sourceRequestId: typesData?.sourceRequestId || typesData?.draftOfferId || queryHandoffId || '',
                category: normalizedCategoryValue,
                subcategory: normalizedSubcategoryValue,
                industry: normalizedCategoryValue,
                preofferDepartmentBoard: typesData?.preofferDepartmentBoard
                    || setupData?.preofferDepartmentBoard
                    || preoffer_department_board_from_types(first_selected_types(typesData?.selectedTypes, setupTypes)),
                preofferTurn: preofferIsRequested ? 'done' : (typesData?.preofferTurn || '')
            };
            const normalizedSetupData = preofferIsRequested
                ? {
                    ...setupData,
                    preofferTurn: 'done'
                }
                : setupData;
            const clientVatNumber = `${normalizedTypesData?.clientVatNumber || ''}`.trim();
            const selectedType = setupTypes?.[0] || '';
            const submittedBy = `${user?.subId || user?.sub || user?.userId || user?.id || user?._id || ''}`.trim();
            const canonicalSetupData = {
                ...normalizedSetupData,
                ...(optionalReviewCreation ? {
                    reviewEnabled,
                    reviewerUserId: reviewEnabled ? normalizedSetupData.reviewerUserId : '',
                    reviewerIsSelf: reviewEnabled && isSelfReviewer
                } : {}),
                requestOfferMode: normalizedTypesData?.requestOfferMode || normalizedSetupData?.requestOfferMode || false,
                preofferEnabled: Boolean(normalizedTypesData?.preofferEnabled || normalizedSetupData?.preofferEnabled),
                preofferAssignedTeamLead: normalizedTypesData?.preofferAssignedTeamLead || normalizedSetupData?.preofferAssignedTeamLead || null,
                preofferRequestedAt: normalizedTypesData?.preofferRequestedAt || normalizedSetupData?.preofferRequestedAt || '',
                preofferRequestedBy: normalizedTypesData?.preofferRequestedBy || normalizedSetupData?.preofferRequestedBy || null,
                preofferTurn: normalizedTypesData?.preofferTurn || normalizedSetupData?.preofferTurn || '',
                preofferEstimateSubmittedAt: normalizedTypesData?.preofferEstimateSubmittedAt || normalizedSetupData?.preofferEstimateSubmittedAt || '',
                preofferEstimateSubmittedBy: normalizedTypesData?.preofferEstimateSubmittedBy || normalizedSetupData?.preofferEstimateSubmittedBy || null,
                preofferDepartmentBoard: normalizedTypesData?.preofferDepartmentBoard || normalizedSetupData?.preofferDepartmentBoard || ''
            };
            const existingStatusline = Array.isArray(currentOfferDraft?.statusline) && currentOfferDraft.statusline.length
                ? currentOfferDraft.statusline
                : [];
            const nextStatusline = existingStatusline[existingStatusline.length - 1]?.status === 'valid'
                ? existingStatusline
                : [
                    ...existingStatusline,
                    {
                        status: 'valid',
                        at: new Date().toISOString()
                    }
                ];

            const offerPayload = {
                selfReviewConfirmed: !optionalReviewCreation && isSelfReviewer,
                ...(optionalReviewCreation ? { reviewEnabled } : {}),
                ofrId: normalizedTypesData?.offerCode || '',
                selectedType,
                language: normalizedTypesData?.offerLanguage || 'el',
                invoice: normalizedTypesData?.invoiceEnabled === false ? false : true,
                submittedBy,
                deleted: false,
                client: {
                    brand: normalizedTypesData?.clientName || '',
                    emails: normalizedTypesData?.clientEmails || [],
                    numbers: [
                        normalizedTypesData?.contactPhone || '',
                        ...(Array.isArray(normalizedTypesData?.contactPhones) ? normalizedTypesData.contactPhones : [])
                    ].filter(Boolean),
                    category: normalizedCategoryValue,
                    subcategory: normalizedSubcategoryValue,
                    ownerFirstname: normalizedTypesData?.sourceClientFirstname || normalizedTypesData?.clientName || '',
                    ownerLastname: normalizedTypesData?.sourceClientLastname || '',
                    location: normalizedTypesData?.clientHeadquarters || '',
                    vat: clientVatNumber || normalizedTypesData?.sourceClientVat || ''
                },
                typesData: normalizedTypesData,
                setupData: { ...canonicalSetupData, replacementConfirmation: offerContentPayload.replacementConfirmation || null },
                priceData,
                statusline: nextStatusline,
                activity: {
                    ...(currentOfferDraft?.activity || {}),
                    visits: Array.isArray(currentOfferDraft?.activity?.visits)
                        ? currentOfferDraft.activity.visits
                        : Array.isArray(currentOfferDraft?.visits) ? currentOfferDraft.visits : [],
                    actionLog: Array.isArray(currentOfferDraft?.activity?.actionLog)
                        ? currentOfferDraft.activity.actionLog
                        : Array.isArray(currentOfferDraft?.offerUpdates) ? currentOfferDraft.offerUpdates : []
                },
                content: {
                    snapshot: offerContentPayload?.offerContentSnapshot || {},
                    lockedAt: currentOfferDraft?.content?.lockedAt || currentOfferDraft?.offerContentLockedAt || '',
                    template: {
                        key: offerContentPayload?.offerContentTemplateKey || '',
                        fingerprint: offerContentPayload?.offerContentTemplateFingerprint || '',
                        version: currentOfferDraft?.content?.template?.version || currentOfferDraft?.offerContentVersion || {}
                    }
                },
                niffler: currentOfferDraft?.niffler || {}
            };
            const canonicalOfferPayload = canonical_create_offer_payload(offerPayload);
            let updateOfferResponse;

            if (fakeOffer) {
                updateOfferResponse = await create_fake_offer({ ...canonicalOfferPayload, fakeOffer: true, requestId: offerId || typesData.sourceRequestId,
                    replacementConfirmation: offerContentPayload.replacementConfirmation || null });
            } else if (freshAgencyHelpMode) {
                updateOfferResponse = await create_agency_help_offer(canonicalOfferPayload);
            } else if (preofferV2Mode && preofferV2Stage === 'preparer') {
                updateOfferResponse = await submit_agency_preoffer_by_id(offerId, {
                    ...canonicalOfferPayload,
                    preofferIssueId: queryPreofferIssueId
                });
            } else if (offerApiMode === 'legacy') {
                updateOfferResponse = await update_offer_draft_by_id(offerId || currentOfferDraft?.offerId, {
                    ...canonicalOfferPayload,
                    status: 'sent'
                });
            } else if (offerId) {
                updateOfferResponse = await send_agency_offer_by_id(offerId, {
                    ...canonicalOfferPayload,
                    ...(preofferV2Mode ? { preofferIssueId: queryPreofferIssueId } : {})
                });
            } else {
                updateOfferResponse = await create_agency_sent_offer(canonicalOfferPayload);
            }
            const updatedOffer = updateOfferResponse?.data?.data?.offer || updateOfferResponse?.data?.data || {};

            if (updatedOffer.workflow?.turn === 'done' || (!freshAgencyHelpMode && !(preofferV2Mode && preofferV2Stage === 'preparer'))) {
                notify_niffler_offer_completed(updatedOffer);
            }

            clear_offer_related_browser_state();
            navigate(freshAgencyHelpMode || preofferV2Mode ? `/${routeLanguage}/agency` : `/${routeLanguage}`, {
                replace: true
            });
        } catch (error) {
            if (error?.response?.data?.data?.syncPending && error.response.data.data.offer?.offerId) {
                const savedOffer = {
                    ...error.response.data.data.offer,
                    cerebroSyncPending: true
                };
                setPendingSyncOffer(savedOffer);
                setCurrentOfferDraft(savedOffer);
                setOfferId(savedOffer.offerId);
                write_stored_offer_id(savedOffer.offerId);
                write_stored_offer_draft(savedOffer);
                navigate(`/${routeLanguage}/new-offer?offerId=${encodeURIComponent(savedOffer.offerId)}`, { replace: true });
            }
            setDraftError(error?.response?.data?.message || 'Could not create and send offer.');
        } finally {
            submissionInProgress.current = false;
            setIsDraftSaving(false);
        }
    };

    const retry_saved_offer_sync = async() => {
        if (isDraftSaving || !pendingSyncOffer?.offerId) {
            return;
        }

        setIsDraftSaving(true);
        setDraftError('');

        try {
            await retry_agency_preoffer_sync_by_id(pendingSyncOffer.offerId);
            clear_offer_related_browser_state();
            navigate(`/${routeLanguage}/agency`, { replace: true });
        } catch (error) {
            setDraftError(error?.response?.data?.message || t('Could not retry Tasks sync.'));
        } finally {
            setIsDraftSaving(false);
        }
    };

    return (
        <NewOffer>
            {!draftHasFatalLoadError && !pendingSyncOffer ? (
                <Steps_ step={step} setStep={go_step} />
            ) : null}
            <NewOfferContent>
                <PageAlwaysVisible>
                    {isDeleteConfirmationOpen && canShowDeleteButton ? (
                        <Confirmation_
                            message={t('Delete this offer?')}
                            no={t('No')}
                            yes={adminActionType === 'delete' ? t('Deleting...') : t('Yes')}
                            onNo={() => setIsDeleteConfirmationOpen(false)}
                            onYes={delete_current_offer}
                        />
                    ) : null}
                    <PageTitle>
                        <NewOfferTitleRow>
                            <h1>{t('New offer')}</h1>
                            <NewOfferTopActions>
                                {canShowDeleteButton ? (
                                    <Red_
                                        text={adminActionType === 'delete' ? t('Deleting...') : t('Delete')}
                                        onClick={() => setIsDeleteConfirmationOpen(true)}
                                    />
                                ) : null}
                            </NewOfferTopActions>
                        </NewOfferTitleRow>
                    </PageTitle>
                    {draftError ? <p>{draftError}</p> : null}
                    {pendingSyncOffer ? (
                        <>
                            <p>{t('Your offer is saved. Its Tasks assignment is pending.')}</p>
                            <Main_Plain
                                noPadding
                                text={t('Retry Tasks sync')}
                                disabled={isDraftSaving}
                                onClick={retry_saved_offer_sync}
                            ></Main_Plain>
                            <Main_Plain
                                noPadding
                                text={t('Back to offers')}
                                onClick={() => navigate(`/${routeLanguage}/agency`)}
                            ></Main_Plain>
                        </>
                    ) : null}
                    {draftHasFatalLoadError || pendingSyncOffer ? null : mainLoaderIsVisible ? (
                        <NewOfferMainLoader role="status" aria-live="polite">
                            <NewOfferMainLoaderIcon />
                            <p>{mainLoaderText}</p>
                        </NewOfferMainLoader>
                    ) : offerWorkflowReadOnly ? (
                        <div>
                            <p>{t(activeOfferReadOnly ? 'Services are controlled by the active offer. Create a replacement offer to change them.' : 'This offer is read-only because it belongs to another department or workflow owner.')}</p>
                            {activeOfferReadOnly ? <Main_Plain text={t('Create replacement offer')} onClick={() => navigate(`/${routeLanguage}/new-offer`)}></Main_Plain> : null}
                            {currentOfferDraft?.setupData?.servicePolicyVersion === 1 && !currentOfferDraft.setupData.serviceActivation?.state ? <Offer_Creation_Action
                                offer={currentOfferDraft} fakeOffer={currentOfferDraft.setupData.fakeOffer === true} saving={isDraftSaving} text={t('Reconfirm replacement')}
                                onCreate={async confirmation => {
                                    setIsDraftSaving(true);
                                    try { await confirm_offer_replacement(currentOfferDraft._id || currentOfferDraft.offerId, confirmation);
                                        hydrate_offer_draft((await get_offer_draft_by_id(currentOfferDraft.offerId)).data.data, step);
                                    } catch (failure) { setDraftError(failure.response?.data?.message || t('Could not confirm replacement.')); }
                                    finally { setIsDraftSaving(false); }
                                }}
                            ></Offer_Creation_Action> : null}
                        </div>
                    ) : (
                        <>
                            {step === 0 ? (
                                <New_Offer_Types
                                    typesData={typesData}
                                    requestOfferMode={requestOfferSourceMode}
                                    requestOfferSourceMode={requestOfferSourceMode}
                                    lockRequestOfferFields={requestOfferSourceMode && continueMode}
                                    lockOfferType={preofferV2Mode}
                                    enforceClientEligibility={freshAgencyHelpMode}
                                    onNext={go_next}
                                    exit={go_back}
                                />
                            ) : null}
                            {step === 1 ? (
                                <New_Offer_Setup
                                    selectedTypes={setupTypes}
                                    typesData={typesData}
                                    setupData={setupData}
                                    priceData={priceData}
                                    showClientContext={continueMode}
                                    requestOfferMode={requestOfferSourceMode && !preofferV2Mode}
                                    requestOfferSourceMode={requestOfferSourceMode}
                                    requestOfferSubmitMode={false}
                                    allowPreoffer={agencyOfferWorkflowsEnabled}
                                    offerWorkflowMode={offerWorkflowMode}
                                    managerReviewReadOnly={legacyPreofferIssueReview.readOnly}
                                    askForHelpOnPreview={freshAgencyHelpMode}
                                    allowReview={freshAgencyHelpMode}
                                    onBack={go_back}
                                    onNext={go_next}
                                    onAskForHelp={request_preoffer_help}
                                    onSubmitEstimate={submit_preoffer_estimate}
                                />
                            ) : null}
                            {step === 2 ? (
                                <New_Offer_Price
                                    allowFakeOffer={!departmentManagerEstimateMode && !(preofferV2Mode && preofferV2Stage === 'preparer')}
                                    fakeOffer={fakeOffer}
                                    onFakeOfferChange={value => {
                                        setFakeOffer(value);
                                        const draft = read_stored_offer_draft();
                                        if (draft) write_stored_offer_draft({ ...draft, fakeOffer: value });
                                    }}
                                    step={step}
                                    onBack={go_back}
                                    onNext={go_next}
                                    selectedTypes={setupTypes}
                                    typesData={typesData}
                                    setupData={setupData}
                                    priceData={priceData}
                                />
                            ) : null}
                            {step === 3 ? (
                                <New_Offer_Preview_Page
                                    allowFakeOffer={!departmentManagerEstimateMode && !(preofferV2Mode && preofferV2Stage === 'preparer')}
                                    fakeOffer={fakeOffer}
                                    offerDraft={{
                                        offerId,
                                        step,
                                        selectedTypes: setupTypes,
                                        clientEmails: typesData?.clientEmails || [],
                                        typesData,
                                        setupData,
                                        priceData,
                                        createdAt: offerCreatedAt,
                                        status: 'ready_for_review',
                                        active: true
                                    }}
                                    isSaving={isDraftSaving}
                                    onBack={go_back}
                                    onCreateAndSend={create_and_send_offer}
                                    onApplyReviewerPrices={apply_reviewer_prices}
                                    showReviewerSuggestedPricingNote={growthContinuationMode
                                        || (preofferV2Mode && ['preparer', 'creator'].includes(preofferV2Stage))}
                                    confirmReviewerPriceDifference={growthContinuationMode
                                        || (preofferV2Mode && preofferV2Stage === 'creator')}
                                    actionText={freshAgencyHelpMode
                                        ? 'Submit'
                                        : preofferV2Stage === 'preparer' ? 'Review' : preofferV2Stage === 'creator' ? 'Create' : 'Create and Send'}
                                    savingActionText={freshAgencyHelpMode
                                        ? 'Submitting...'
                                        : preofferV2Stage === 'preparer' ? 'Reviewing...' : 'Creating...'}
                                />
                            ) : null}
                        </>
                    )}
                </PageAlwaysVisible>
            </NewOfferContent>
        </NewOffer>
    );
}
